/**
 * Admin detection. Replaces the hardcoded `0535558022` (used in messaging) and
 * `0595558022` (used in auth as a test bypass) with a Firebase-backed config
 * at `/config/admins/`:
 *
 *   /config/admins/uids/{uid}     = true   ← admin user IDs
 *   /config/admins/phones/{phone} = true   ← admin phones (canonical form)
 *
 * Updates the cache live via a `.on('value')` subscription so privilege
 * changes propagate without a client restart.
 */

import { Auth } from '../../app/config.js';
import { ref } from '../../core/firebase.js';
import { normalize as normalizePhone } from '../../shared/phone.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('admin');

/** @type {Set<string>} */
let adminUids = new Set();
/** @type {Set<string>} */
let adminPhones = new Set();

let subscribed = false;
/** @type {any | null} */
let listenerRef = null;
/** @type {((s: any) => void) | null} */
let listenerCallback = null;
let initialLoadResolved = false;
/** @type {Promise<void> | null} */
let initialLoadPromise = null;

/**
 * Subscribe to `/config/admins` live updates. Returns a promise that resolves
 * when the first snapshot has loaded so callers can `await` admin readiness.
 * Safe to call repeatedly — only one subscription is ever active.
 * @returns {Promise<void>}
 */
export function ensureLoaded() {
  if (initialLoadResolved) return Promise.resolve();
  if (initialLoadPromise) return initialLoadPromise;

  initialLoadPromise = new Promise((resolve) => {
    listenerRef = ref(Auth.adminConfigPath);
    listenerCallback = (/** @type {any} */ snap) => {
      const val = snap.val() ?? {};
      adminUids = new Set(Object.keys(val.uids ?? {}));
      adminPhones = new Set(
        Object.keys(val.phones ?? {}).map((p) => normalizePhone(p)),
      );
      if (!initialLoadResolved) {
        initialLoadResolved = true;
        log.info('loaded:', adminUids.size, 'uids,', adminPhones.size, 'phones');
        resolve();
      }
    };
    listenerRef.on('value', listenerCallback, (/** @type {Error} */ err) => {
      log.warn('admin config read denied — defaulting to no admins:', err.message);
      if (!initialLoadResolved) {
        initialLoadResolved = true;
        resolve();
      }
    });
  });

  subscribed = true;
  return initialLoadPromise;
}

/**
 * @param {string | null | undefined} uid
 * @returns {boolean}
 */
export function isAdminUid(uid) {
  return !!uid && adminUids.has(uid);
}

/**
 * @param {string | null | undefined} phone
 * @returns {boolean}
 */
export function isAdminPhone(phone) {
  if (!phone) return false;
  return adminPhones.has(normalizePhone(phone));
}

/** Stop listening and clear cached admin lists. */
export function dispose() {
  if (listenerRef && listenerCallback) {
    try { listenerRef.off('value', listenerCallback); } catch { /* ignore */ }
  }
  listenerRef = null;
  listenerCallback = null;
  subscribed = false;
  initialLoadResolved = false;
  initialLoadPromise = null;
  adminUids = new Set();
  adminPhones = new Set();
}
